import { formattingLocales } from '../utils/formattingLocale';
import {
    IonButton,
    IonButtons,
    IonContent,
    IonDatetime,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonSkeletonText,
    IonText,
    IonTitle,
    IonToolbar,
    useIonActionSheet
} from '@ionic/react';
import { cameraOutline, copyOutline, createOutline, eyeOutline, lockClosedOutline, shieldOutline } from 'ionicons/icons';
import { BarChart } from '@mui/x-charts/BarChart';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { format, startOfMonth, subDays, subMonths } from 'date-fns';
import { useRfidProfileMutation, useRfidProfileQuery } from '../query/rfidProfileQuery';
import {
    useRfidProfileAvatarDeleteMutation,
    useRfidProfileAvatarQuery,
    useRfidProfileAvatarUploadMutation
} from '../query/rfidProfileAvatarQuery';
import { useCatActivityStatsQuery, useCatMonthlyActivityStatsQuery } from '../query/activityStatsQuery';
import { useCurrentUserQuery } from '../query/currentUserQuery';
import { useDevicesQuery } from '../query/devicesQuery';
import { useRfidLastSeenQuery } from '../query/rfidLastSeenQuery';
import { monthlyTransitCounts, summarizeCatActivity } from '../utils/activityStatsSummary';
import { RFID_PROFILE_SEX_VALUES, RfidProfileSex } from '../models/RfidProfile';
import { Device } from '../models/Device';
import Timestamp from '../components/Timestamp';
import useUX from '../utils/ux';
import { useCommonTranslation, useComponentTranslation, useModalTranslation, usePageTranslation } from '../hooks/useTranslation';
import styles from './RfidProfileModal.module.css';

const STATS_WINDOW_DAYS = 30;
// The monthly chart spans this many calendar months, ending with the current
// one. The fetch starts on the 1st of the earliest month so its bar covers a
// whole month instead of however many days a rolling year happens to leave in
// it (worst case 365 days; the gateway caps ranges at 400).
const STATS_MONTHLY_MONTHS = 12;
const MAX_STATS_DEVICE_IDS = 20;
const AVATAR_MAX_DIMENSION = 1024;

const userLocale = () => formattingLocales();

/** 'YYYY-MM-DD' from a profile date value that may be a Date or ISO/day string. */
function toDayString(value: Date | string | null | undefined): string | null {
    if (!value) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}

function formatDay(day: string, options: Intl.DateTimeFormatOptions): string {
    return new Intl.DateTimeFormat(userLocale(), { ...options, timeZone: 'UTC' }).format(new Date(`${day}T00:00:00Z`));
}

function formatMonth(month: string, options: Intl.DateTimeFormatOptions): string {
    return formatDay(`${month}-01`, options);
}

const formatHour = (hour: number) => `${String(hour % 24).padStart(2, '0')}:00`;

/** Weekday names Monday-first; 2024-01-01 is a Monday. */
function weekdayNames(style: 'narrow' | 'long'): string[] {
    const formatter = new Intl.DateTimeFormat(userLocale(), { weekday: style, timeZone: 'UTC' });
    return Array.from({ length: 7 }, (_, index) => formatter.format(new Date(Date.UTC(2024, 0, 1 + index))));
}

/** Downscale a picked image to a JPEG the gateway's 1 MiB avatar limit fits. */
async function downscaleImage(file: File): Promise<Blob> {
    const url = URL.createObjectURL(file);
    try {
        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
            const element = new Image();
            element.onload = () => resolve(element);
            element.onerror = () => reject(new Error('Could not read the selected image.'));
            element.src = url;
        });

        const scale = Math.min(1, AVATAR_MAX_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        canvas.getContext('2d')!.drawImage(image, 0, 0, canvas.width, canvas.height);

        return await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not process the selected image.'))), 'image/jpeg', 0.85);
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <section className={styles.section}>
        <div className={styles.sectionLabel}>{title}</div>
        {children}
    </section>
);

const StatTile: React.FC<{ value: number; label: string; sub: string; icon: string }> = ({ value, label, sub, icon }) => (
    <div className={styles.card + ' ' + styles.statTile}>
        <span className={styles.statTileIcon}><IonIcon icon={icon} /></span>
        <div>
            <div className={styles.statTileValue}>{value}</div>
            <div className={styles.mutedSmall}>{label}</div>
            <div className={styles.statTileSub}>{sub}</div>
        </div>
    </div>
);

// Same chart library as the device telemetry charts (DeviceHealthModal),
// skinned to the pet profile design: peak bar in primary, the rest tinted.
const StatBars: React.FC<{
    values: number[];
    /** Unique category names; shown in the tooltip. */
    categories: string[];
    /** Axis tick text per category ('' hides the tick). */
    tickLabel: (category: string, index: number) => string;
    borderRadius: number;
    height: number;
    categoryGapRatio: number;
}> = ({ values, categories, tickLabel, borderRadius, height, categoryGapRatio }) => {
    const max = Math.max(...values);
    return (
        <BarChart
            height={height}
            // Side margins leave room for the edge tick labels, which MUI
            // otherwise ellipsizes against the drawing-area bounds.
            margin={{ top: 4, bottom: 0, left: 8, right: 8 }}
            hideLegend
            borderRadius={borderRadius}
            series={[{ data: values }]}
            xAxis={[{
                scaleType: 'band',
                data: categories,
                categoryGapRatio,
                disableLine: true,
                disableTicks: true,
                // Most hour ticks are blank; without this the auto interval
                // also drops the edge labels (00/23) as "overlapping".
                tickLabelInterval: () => true,
                valueFormatter: (value: string, context: { location: string }) => (
                    context.location === 'tick' ? tickLabel(value, categories.indexOf(value)) : value
                ),
                colorMap: {
                    type: 'ordinal',
                    colors: values.map((value) => (
                        value === max ? 'var(--ion-color-primary)' : 'rgba(var(--ion-color-primary-rgb), 0.22)'
                    ))
                }
            }]}
            yAxis={[{ position: 'none' }]}
            sx={{ '& .MuiChartsAxis-tickLabel': { fill: 'var(--ion-color-medium)', fontSize: 10.5 } }}
        />
    );
};

const RfidProfileModal = ({ dismiss, rfidCode }: { dismiss: (data?: string | null | undefined | number, role?: string) => void, rfidCode: string }) => {
    const query = useRfidProfileQuery(rfidCode);
    const mutation = useRfidProfileMutation();
    const { presentResult } = useUX();
    const [presentActionSheet] = useIonActionSheet();

    // i18n namespaces: common (buttons), modals (modal-specific), components (field labels), pages (subevent verbs)
    const { t: tCommon } = useCommonTranslation();
    const { t: tModals } = useModalTranslation();
    const { t: tComponents } = useComponentTranslation();
    const { t: tPages } = usePageTranslation();

    const profile = query.data;

    // ion-content measures its fullscreen header offset while the modal is
    // still presenting and records 0; retrigger the measurement once the
    // modal is up so the hero gradient extends beneath the translucent
    // toolbar (which tints the iOS frosting until the hero scrolls away).
    const contentRef = useRef<HTMLIonContentElement>(null);
    useEffect(() => {
        const content = contentRef.current;
        if (!content) {
            return;
        }

        const remeasure = () => {
            content.fullscreen = false;
            content.fullscreen = true;
        };

        const modal = content.closest('ion-modal');
        modal?.addEventListener('ionModalDidPresent', remeasure, { once: true });
        const fallbackTimer = setTimeout(remeasure, 600);

        return () => {
            modal?.removeEventListener('ionModalDidPresent', remeasure);
            clearTimeout(fallbackTimer);
        };
    }, []);

    const [label, setLabel] = useState(profile?.label);
    const [dateOfBirth, setDateOfBirth] = useState<string | null>(toDayString(profile?.dateOfBirth));
    const [sex, setSex] = useState<RfidProfileSex | null>(profile?.sex ?? null);

    useEffect(() => {
        setLabel(profile?.label);
        setDateOfBirth(toDayString(profile?.dateOfBirth));
        setSex(profile?.sex ?? null);
    }, [profile?.label, profile?.dateOfBirth, profile?.sex]);

    // Inline name input sized to its content via a hidden mirror span. The
    // mirror measures 0 while the modal is still presenting (its page isn't
    // laid out yet), so a one-shot measurement would leave the input at the
    // 64px floor; the observer re-fires once layout lands and again if the
    // font swap changes the text metrics.
    const nameMirrorRef = useRef<HTMLSpanElement>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const [nameWidth, setNameWidth] = useState(80);
    useLayoutEffect(() => {
        setNameWidth(nameMirrorRef.current?.offsetWidth ?? 80);
    }, [label]);
    useLayoutEffect(() => {
        const mirror = nameMirrorRef.current;
        if (!mirror) {
            return;
        }
        const observer = new ResizeObserver(() => setNameWidth(mirror.offsetWidth));
        observer.observe(mirror);
        return () => observer.disconnect();
    }, []);

    // Avatar. The merged profile names the household member whose photo is
    // displayed (avatarUserId) - not necessarily the profile row's owner.
    const avatarQuery = useRfidProfileAvatarQuery(profile?.avatarUserId ?? undefined, rfidCode, profile?.avatarUpdatedAt);
    const avatarUploadMutation = useRfidProfileAvatarUploadMutation();
    const avatarDeleteMutation = useRfidProfileAvatarDeleteMutation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const avatarUrl = avatarQuery.data ?? null;

    // Stats across every device the user can see (rollup request cap: 20).
    // Admins query fleet-wide by chip instead, so the profile resolves for
    // cats on devices they do not own (support lookups, shared-event links).
    const devicesQuery = useDevicesQuery();
    const currentUserQuery = useCurrentUserQuery();
    const fleetWideStats = currentUserQuery.data?.userLevel === 'ADMIN';
    const deviceIds = useMemo(
        () => (devicesQuery.data?.pages.flat() ?? []).map((device: Device) => device.deviceId).slice(0, MAX_STATS_DEVICE_IDS),
        [devicesQuery.data]
    );

    // Fetch double the window so the previous 30 days feed the trend line.
    const today = new Date();
    const toDay = format(today, 'yyyy-MM-dd');
    const windowFromDay = format(subDays(today, STATS_WINDOW_DAYS - 1), 'yyyy-MM-dd');
    const fetchFromDay = format(subDays(today, STATS_WINDOW_DAYS * 2 - 1), 'yyyy-MM-dd');
    const monthlyFromDay = format(startOfMonth(subMonths(today, STATS_MONTHLY_MONTHS - 1)), 'yyyy-MM-dd');
    const statsQuery = useCatActivityStatsQuery(rfidCode, deviceIds, fetchFromDay, toDay, { fleetWide: fleetWideStats });
    const monthlyStatsQuery = useCatMonthlyActivityStatsQuery(rfidCode, deviceIds, monthlyFromDay, toDay, { fleetWide: fleetWideStats });

    const stats = useMemo(
        () => (statsQuery.data ? summarizeCatActivity(statsQuery.data.days, windowFromDay, toDay) : null),
        [statsQuery.data, windowFromDay, toDay]
    );

    const monthly = useMemo(
        () => (monthlyStatsQuery.data ? monthlyTransitCounts(monthlyStatsQuery.data.months, toDay) : []),
        [monthlyStatsQuery.data, toDay]
    );

    // Presence: most recent last-seen entry for this chip across devices.
    const lastSeenQuery = useRfidLastSeenQuery();
    const lastSeen = lastSeenQuery.data?.find((item) => item.rfidCode === rfidCode);
    const lastSeenStatus = (() => {
        if (!lastSeen) {
            return null;
        }
        if (lastSeen.locationSource === 'MANUAL' && lastSeen.location) {
            return tComponents(`activity_overview_tile.${lastSeen.location === 'INSIDE' ? 'marked_inside' : 'marked_outside'}`);
        }
        if (lastSeen.lastSubevent) {
            return tPages(`event.timeline.subevents.${lastSeen.lastSubevent.action}_${lastSeen.lastSubevent.direction}`);
        }
        return null;
    })();

    const dirty = (label ?? '') !== (profile?.label ?? '')
        || dateOfBirth !== toDayString(profile?.dateOfBirth)
        || (sex ?? null) !== (profile?.sex ?? null);

    const canSave = dirty && !mutation.isPending;

    const save = async () => {
        presentResult(async () => {
            // Every field is an independent contribution to the merged
            // profile: null clears yours (the household falls back to
            // another member's value), absent leaves it untouched.
            await mutation.mutateAsync({
                rfidCode,
                ...((label ?? '') !== (profile?.label ?? '') && { label: label?.trim() || null }),
                ...(dateOfBirth !== toDayString(profile?.dateOfBirth) && { dateOfBirth }),
                ...((sex ?? null) !== (profile?.sex ?? null) && { sex })
            });
            dismiss(label, 'confirm');
        }, tModals('rfid_profile.profile_updated'));
    };

    const pickPhoto = () => fileInputRef.current?.click();

    const onAvatarClick = () => {
        if (!avatarUrl) {
            pickPhoto();
            return;
        }

        presentActionSheet({
            buttons: [
                { text: tModals('rfid_profile.change_photo'), handler: pickPhoto },
                {
                    text: tModals('rfid_profile.remove_photo'),
                    role: 'destructive',
                    handler: () => {
                        presentResult(
                            () => avatarDeleteMutation.mutateAsync({ rfidCode }),
                            tModals('rfid_profile.photo_removed')
                        );
                    }
                },
                { text: tCommon('buttons.cancel'), role: 'cancel' }
            ]
        });
    };

    const onFilePicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }

        presentResult(async () => {
            const image = await downscaleImage(file);
            await avatarUploadMutation.mutateAsync({ rfidCode, image });
        }, tModals('rfid_profile.photo_updated'));
    };

    const copyMicrochipCode = async () => {
        if (navigator.clipboard?.writeText) {
            presentResult(
                () => navigator.clipboard.writeText(rfidCode),
                tModals('rfid_profile.microchip_copied')
            );
        }
    };

    const dobModalRef = useRef<HTMLIonModalElement>(null);
    const displayName = label?.trim();
    const weekdayLetters = useMemo(() => weekdayNames('narrow'), []);
    // Band-scale categories must be unique, so ticks use the letters above
    // while the categories (and tooltips) carry the full names.
    const weekdayCategories = useMemo(() => weekdayNames('long'), []);
    const hourCategories = useMemo(() => Array.from({ length: 24 }, (_, hour) => formatHour(hour)), []);
    const trendPct = stats?.transitsTrendPct;

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="medium" onClick={() => dismiss(null, 'cancel')}>
                            {tCommon('buttons.cancel')}
                        </IonButton>
                    </IonButtons>
                    <IonTitle>{tModals('rfid_profile.title')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={save} strong={true} disabled={!canSave}>
                            {tCommon('buttons.save')}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent ref={contentRef} fullscreen color="light">
                <div className={styles.hero}>
                    <button type="button" className={styles.avatar} onClick={onAvatarClick} aria-label={tModals('rfid_profile.add_photo')}>
                        {avatarUrl
                            ? <img src={avatarUrl} alt="" className={styles.avatarImage} />
                            : (
                                <span className={styles.avatarPlaceholder}>
                                    <IonIcon src="/assets/activity/activity-icon.svg" className={styles.avatarPlaceholderMark} aria-hidden />
                                    <span className={styles.avatarPlaceholderLabel}>{tModals('rfid_profile.add_photo')}</span>
                                </span>
                            )}
                        <span className={styles.avatarBadge}><IonIcon icon={cameraOutline} /></span>
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={onFilePicked} hidden />

                    <div className={styles.nameRow}>
                        <span ref={nameMirrorRef} className={styles.nameMirror} aria-hidden>
                            {label || tComponents('rfid_profile.name_placeholder')}
                        </span>
                        <input
                            ref={nameInputRef}
                            className={styles.nameInput}
                            style={{ width: Math.min(280, Math.max(64, nameWidth + 4)) }}
                            value={label ?? ''}
                            maxLength={32}
                            placeholder={tComponents('rfid_profile.name_placeholder')}
                            aria-label={tComponents('rfid_profile.name')}
                            onChange={(event) => setLabel(event.target.value || undefined)}
                        />
                        <button type="button" className={styles.namePencil} aria-hidden tabIndex={-1} onClick={() => nameInputRef.current?.focus()}>
                            <IonIcon icon={createOutline} />
                        </button>
                    </div>

                    {lastSeenStatus && (
                        <div className={styles.lastSeen}>
                            {lastSeenStatus}
                            {' · '}
                            <Timestamp timestamp={lastSeen?.locationUpdatedAt ?? lastSeen?.eventTimestamp} short />
                        </div>
                    )}

                    {lastSeen?.location && (
                        <span className={`${styles.locationPill} ${lastSeen.location === 'INSIDE' ? styles.locationPillInside : styles.locationPillOutside}`}>
                            <span className={styles.locationDot} />
                            {tModals(`rfid_profile.${lastSeen.location === 'INSIDE' ? 'inside' : 'outside'}`)}
                        </span>
                    )}
                </div>

                <div className={styles.body}>
                    <Section title={tModals('rfid_profile.last_30_days')}>
                        {statsQuery.isLoading && (fleetWideStats || deviceIds.length > 0) && (
                            <>
                                <IonSkeletonText animated className={styles.cardSkeleton} />
                                <IonSkeletonText animated className={styles.cardSkeleton} />
                            </>
                        )}

                        {stats && !stats.hasActivity && (
                            <div className={`${styles.card} ${styles.emptyCard}`}>{tModals('rfid_profile.no_activity')}</div>
                        )}

                        {stats && stats.hasActivity && (
                            <>
                                <div className={`${styles.card} ${styles.preyCard}`}>
                                    <span className={styles.preyIcon}><IonIcon icon={shieldOutline} /></span>
                                    <div className={styles.preyBody}>
                                        <div className={styles.preyHeadline}>
                                            <span className={styles.bigNumber}>{stats.preyAttempts}</span>
                                            <span className={styles.preyLabel}>{tModals('rfid_profile.contraband_detected', { count: stats.preyAttempts })}</span>
                                        </div>
                                        <div className={styles.mutedSmall}>
                                            {stats.lastPreyDay && stats.daysSinceLastPrey !== null
                                                ? tModals('rfid_profile.last_prey', {
                                                    date: formatDay(stats.lastPreyDay, { day: 'numeric', month: 'short' }),
                                                    count: stats.daysSinceLastPrey
                                                })
                                                : tModals('rfid_profile.no_contraband')}
                                        </div>
                                    </div>
                                </div>

                                <div className={`${styles.card} ${styles.transitsCard}`}>
                                    <div className={styles.transitsHeadline}>
                                        <span className={styles.bigNumber}>{stats.transits}</span>
                                        <span className={styles.mutedSmall}>{tModals('rfid_profile.transits', { count: stats.transits })}</span>
                                        {trendPct !== null && trendPct !== undefined && (
                                            <span className={`${styles.trend} ${trendPct >= 0 ? styles.trendUp : ''}`}>
                                                {tModals('rfid_profile.vs_previous_month', { trend: `${trendPct >= 0 ? '+' : ''}${trendPct}%` })}
                                            </span>
                                        )}
                                    </div>
                                    {stats.transits > 0 && (
                                        <div className={styles.inOutSplit}>
                                            <div style={{ flex: Math.max(stats.transitsOut, 1) }}>
                                                <div className={`${styles.inOutBar} ${styles.inOutBarOut}`} />
                                                <div className={styles.mutedSmall}>
                                                    {tModals('rfid_profile.went_out')} <strong>{stats.transitsOut}</strong>
                                                </div>
                                            </div>
                                            <div style={{ flex: Math.max(stats.transitsIn, 1) }}>
                                                <div className={`${styles.inOutBar} ${styles.inOutBarIn}`} />
                                                <div className={styles.mutedSmall}>
                                                    {tModals('rfid_profile.came_in')} <strong>{stats.transitsIn}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className={styles.tileGrid}>
                                    <StatTile
                                        value={stats.denies}
                                        label={tModals('rfid_profile.denies_label')}
                                        sub={tModals('rfid_profile.denies_sub')}
                                        icon={lockClosedOutline}
                                    />
                                    <StatTile
                                        value={stats.peeks}
                                        label={tModals('rfid_profile.peeks_label')}
                                        sub={tModals('rfid_profile.peeks_sub')}
                                        icon={eyeOutline}
                                    />
                                </div>
                            </>
                        )}
                    </Section>

                    {stats && stats.hasActivity && stats.transits > 0 && (
                        <>
                            <Section title={displayName
                                ? tModals('rfid_profile.flap_usage', { name: displayName })
                                : tModals('rfid_profile.flap_usage_unnamed')}>
                                <div className={`${styles.card} ${styles.chartCard}`}>
                                    <div className={styles.chartHeadline}>
                                        <span className={styles.chartTitle}>{stats.busiestHour !== null ? formatHour(stats.busiestHour) : ''}</span>
                                        <span className={styles.mutedSmall}>{tModals('rfid_profile.busiest_hour')}</span>
                                    </div>
                                    <StatBars
                                        values={stats.hourHistogram}
                                        categories={hourCategories}
                                        tickLabel={(_category, index) => (index % 6 === 0 || index === 23 ? String(index).padStart(2, '0') : '')}
                                        borderRadius={3}
                                        height={110}
                                        categoryGapRatio={0.35}
                                    />
                                    {stats.coreHours && (
                                        <div className={`${styles.mutedSmall} ${styles.coreHoursNote}`}>
                                            {tModals('rfid_profile.core_hours', {
                                                pct: stats.coreHours.pct,
                                                from: formatHour(stats.coreHours.startHour),
                                                to: formatHour(stats.coreHours.endHourExclusive)
                                            })}
                                        </div>
                                    )}
                                </div>
                            </Section>

                            <Section title={tModals('rfid_profile.transits_by_weekday')}>
                                <div className={`${styles.card} ${styles.chartCard}`}>
                                    <div className={styles.chartHeadline}>
                                        <span className={styles.chartTitle}>{stats.busiestWeekday !== null ? weekdayNames('long')[stats.busiestWeekday] : ''}</span>
                                        <span className={styles.mutedSmall}>
                                            {tModals('rfid_profile.busiest_day_sub', { avg: stats.avgTransitsPerDay.toLocaleString(userLocale(), { maximumFractionDigits: 1 }) })}
                                        </span>
                                    </div>
                                    <StatBars
                                        values={stats.weekdayCounts}
                                        categories={weekdayCategories}
                                        tickLabel={(_category, index) => weekdayLetters[index]}
                                        borderRadius={7}
                                        height={120}
                                        categoryGapRatio={0.25}
                                    />
                                </div>
                            </Section>

                            {monthly.length >= 2 && (
                                <Section title={tModals('rfid_profile.transits_by_month')}>
                                    <div className={`${styles.card} ${styles.chartCard}`}>
                                        <div className={styles.chartHeadline}>
                                            <span className={styles.chartTitle}>
                                                {formatMonth(monthly.reduce((max, entry) => (entry.transits > max.transits ? entry : max)).month, { month: 'long' })}
                                            </span>
                                            <span className={styles.mutedSmall}>{tModals('rfid_profile.busiest_month')}</span>
                                        </div>
                                        <StatBars
                                            values={monthly.map((entry) => entry.transits)}
                                            categories={monthly.map((entry) => formatMonth(entry.month, { month: 'long', year: 'numeric' }))}
                                            tickLabel={(_category, index) => formatMonth(monthly[index].month, { month: monthly.length > 6 ? 'narrow' : 'short' })}
                                            borderRadius={7}
                                            height={120}
                                            categoryGapRatio={0.25}
                                        />
                                    </div>
                                </Section>
                            )}

                            <Section title={tModals('rfid_profile.records')}>
                                <IonList inset={false} className={styles.listCard}>
                                    {stats.mostActiveDay && (
                                        <IonItem>
                                            <IonLabel>{tModals('rfid_profile.most_active_day')}</IonLabel>
                                            <IonText slot="end" color="medium">
                                                {formatDay(stats.mostActiveDay.day, { weekday: 'short', day: 'numeric', month: 'short' })}
                                                {' · '}{stats.mostActiveDay.transits}
                                            </IonText>
                                        </IonItem>
                                    )}
                                    <IonItem lines="none">
                                        <IonLabel>{tModals('rfid_profile.prey_free_streak')}</IonLabel>
                                        <IonText slot="end" color="medium">
                                            {stats.daysSinceLastPrey !== null
                                                ? tModals('rfid_profile.days_count', { count: stats.daysSinceLastPrey })
                                                : tModals('rfid_profile.days_count_plus', { count: STATS_WINDOW_DAYS * 2 })}
                                        </IonText>
                                    </IonItem>
                                </IonList>
                            </Section>
                        </>
                    )}

                    <Section title={tModals('rfid_profile.details')}>
                        <IonList inset={false} className={styles.listCard}>
                            <IonItem button detail id="rfid-profile-dob-row">
                                <IonLabel>
                                    <p>{tModals('rfid_profile.date_of_birth')}</p>
                                    <h3 className={dateOfBirth ? undefined : styles.placeholderValue}>
                                        {dateOfBirth
                                            ? formatDay(dateOfBirth, { dateStyle: 'long' })
                                            : tModals('rfid_profile.add_date_of_birth')}
                                    </h3>
                                </IonLabel>
                            </IonItem>
                            <IonItem>
                                <IonSelect
                                    label={tModals('rfid_profile.sex')}
                                    labelPlacement="stacked"
                                    interface="action-sheet"
                                    placeholder={tModals('rfid_profile.add_sex')}
                                    value={sex ?? undefined}
                                    onIonChange={(event) => setSex((event.detail.value || null) as RfidProfileSex | null)}
                                >
                                    {RFID_PROFILE_SEX_VALUES.map((value) => (
                                        <IonSelectOption key={value} value={value}>
                                            {tModals(`rfid_profile.sex_${value.toLowerCase()}`)}
                                        </IonSelectOption>
                                    ))}
                                    {sex && <IonSelectOption value="">{tModals('rfid_profile.sex_unspecified')}</IonSelectOption>}
                                </IonSelect>
                            </IonItem>
                            <IonItem lines="none">
                                <IonLabel>
                                    <p>{tComponents('rfid_profile.microchip_code')}</p>
                                    <h3 className={styles.microchipCode}>{rfidCode}</h3>
                                </IonLabel>
                                <IonButton slot="end" fill="clear" color="medium" onClick={copyMicrochipCode} aria-label={tModals('rfid_profile.copy_microchip_code')}>
                                    <IonIcon slot="icon-only" icon={copyOutline} />
                                </IonButton>
                            </IonItem>
                        </IonList>
                    </Section>
                </div>

                <IonModal ref={dobModalRef} trigger="rfid-profile-dob-row" className={styles.datetimeModal}>
                    <IonDatetime
                        presentation="date"
                        preferWheel
                        value={dateOfBirth ?? undefined}
                        max={toDay}
                        showDefaultButtons
                        showClearButton
                        onIonChange={(event) => {
                            const value = event.detail.value;
                            setDateOfBirth(typeof value === 'string' ? value.slice(0, 10) : null);
                            dobModalRef.current?.dismiss();
                        }}
                        onIonCancel={() => dobModalRef.current?.dismiss()}
                    />
                </IonModal>
            </IonContent>
        </IonPage>
    );
};

export default RfidProfileModal;
